import { FormEvent, useRef, useState } from 'react';
import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { urls } from '@zapier/identity-utils';

import { RouteTo } from '../../types';
import { fetch } from '../../utils';
import {
  GENERIC_ERROR_MESSAGE,
  NEW_DEVICE_RESEND_COOLDOWN_SECONDS,
} from './constants';
import { parseCodeStepResponse } from './errorUtils';
import { NEW_DEVICE_REASONS } from './newDeviceReasons';
import {
  CodeStepResponseBody,
  NewDeviceChallenge,
  NewDeviceCodeAction,
} from './types';

type UseNewDeviceCodeStepInput = {
  next?: string;
  routeTo: RouteTo;
  /**
   * The code was confirmed: the browser is trusted and a session was created
   */
  onConfirmed: () => void;
  /**
   * There's no pending sign-in any more (it expired, or the account changed),
   * so the password step takes over with this message
   */
  onExpired: (message: string) => void;
  /**
   * The user went back to the password step
   */
  onLeave: () => void;
};

type Status = 'idle' | 'submitting' | 'confirmed';

/**
 * The sign-in code step, shown while the backend wants this browser confirmed
 * with an emailed code. The challenged login left a pending sign-in in the
 * session, so each request sends only a code (`submit`) or asks for a new one
 * (`resend`), with a fresh captcha token, and never the password.
 */
export function useNewDeviceCodeStep({
  next,
  routeTo,
  onConfirmed,
  onExpired,
  onLeave,
}: UseNewDeviceCodeStepInput) {
  const [challenge, setChallenge] = useState<NewDeviceChallenge>();
  const [code, setCode] = useState('');
  const [captcha, setCaptcha] = useState('');
  // Bumped to remount (reset) the captcha after each request
  const [captchaKey, setCaptchaKey] = useState(0);
  // Epoch ms when "Resend code" is accepted again
  const [resendAvailableAt, setResendAvailableAt] = useState<number>();
  const [error, setError] = useState<string>();
  const [lastAction, setLastAction] = useState<NewDeviceCodeAction>();
  const [status, setStatus] = useState<Status>('idle');
  // Numbers requests; leaving or restarting the step bumps it, so a response
  // that arrives afterwards is dropped
  const requestId = useRef(0);

  function resetCaptcha() {
    setCaptcha('');
    setCaptchaKey(key => key + 1);
  }

  /**
   * Clears the step and drops any request in flight
   */
  function reset() {
    requestId.current += 1;
    setChallenge(undefined);
    setCode('');
    resetCaptcha();
    setResendAvailableAt(undefined);
    setError(undefined);
    setLastAction(undefined);
    setStatus('idle');
  }

  /**
   * Shows a challenge and works out when a resend is next accepted
   */
  function start(next: NewDeviceChallenge) {
    const outcome = NEW_DEVICE_REASONS[next.reason].code;

    setChallenge(next);
    setStatus('idle');

    if (outcome === 'emailed') {
      setResendAvailableAt(
        Date.now() + NEW_DEVICE_RESEND_COOLDOWN_SECONDS * 1000
      );
    } else if (outcome === 'dead') {
      setResendAvailableAt(undefined);
    }

    // A new code is on its way, or the typed one is dead: clear the field
    if (outcome !== 'kept') {
      setCode('');
    }
  }

  function leave() {
    reset();
    onLeave();
  }

  async function send(action: NewDeviceCodeAction) {
    if (action === 'submit' && !code.trim()) {
      setError('Enter the code from the email.');
      return;
    }
    if (!captcha) {
      setError(
        action === 'resend'
          ? 'Complete the captcha to send a new code.'
          : 'Complete the captcha to continue.'
      );
      return;
    }

    setStatus('submitting');
    setError(undefined);
    setLastAction(action);

    const id = ++requestId.current;
    const isCurrent = () => id === requestId.current;

    try {
      // No logout first: that would drop the pending sign-in
      const response = await fetch<CodeStepResponseBody>(
        action === 'submit'
          ? urls.LOGIN_DEVICE_CONFIRMATIONS_API_URL
          : urls.LOGIN_DEVICE_CODES_API_URL,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body:
            action === 'submit'
              ? { device_code: code.trim(), captcha }
              : { captcha },
        }
      );

      // The user left the step while this request was in flight
      if (!isCurrent()) return;

      const result = parseCodeStepResponse(response, action);

      if (result.type === 'confirmed') {
        // Keep the step up while the page navigates, as the password step does
        setStatus('confirmed');
        onConfirmed();
      } else if (result.type === 'challenge') {
        start(result.challenge);
      } else if (result.type === 'expired') {
        reset();
        onExpired(result.message);
      } else if (result.type === 'redirect') {
        // Keep the step up while the page navigates
        setStatus('confirmed');
        routeTo(addTruthyParamsToUrl(result.url, { next }));
      } else {
        setError(result.message);
        setStatus('idle');
      }
    } catch (err) {
      if (!isCurrent()) return;
      setError(GENERIC_ERROR_MESSAGE);
      setStatus('idle');
    } finally {
      // Each token is good for one request. After Back, reset() already
      // cleared this one, and the captcha now belongs to the next challenge.
      if (isCurrent()) resetCaptcha();
    }
  }

  return {
    // Set while the backend wants this browser confirmed
    challenge,
    code,
    captchaKey,
    // An error from the last request (captcha, throttle, network)
    error,
    hasCaptcha: Boolean(captcha),
    // Busy while a request runs, and after a confirmed code while the page
    // navigates
    isSubmitting: status !== 'idle',
    lastAction,
    resendAvailableAt,
    setCode: (value: string) => setCode(value),
    setCaptcha: (token: string | null) => setCaptcha(token || ''),
    submit: async (e?: FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      await send('submit');
    },
    resend: () => send('resend'),
    leave,
    // For useLogin: show a challenge from the password step, or clear the step
    start,
    reset,
  };
}

export type NewDeviceCodeStep = ReturnType<typeof useNewDeviceCodeStep>;
